import React, { useEffect, useRef, useState } from "react";
import { Trophy, RotateCcw, LogOut, Download, Link as LinkIcon, Crown, Clock, Users, Film, Share2, Globe2, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ClipReactions from "@/components/ClipReactions";

const MEDALS = ["🥇", "🥈", "🥉"];

export default function CoopResults({ open, onOpenChange, room, user, gameCover, gameTitle, isHost, onRematch, onLeave, coopMode = false, squadStats = {}, mvpClipUrl, mvpClipMime, mvpClipBlob, squadClip, onClipPosted }) {
  // Persist a Boss-MVP win to the profile when the local player leads the squad in boss damage.
  const mvpPostedRef = useRef(false);
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | done
  useEffect(() => {
    if (!open) { mvpPostedRef.current = false; setUploadState("idle"); return; }
    if (!coopMode || mvpPostedRef.current) return;
    const entries = Object.entries(squadStats || {});
    if (entries.length < 2) return;
    let mvpUid = null, best = -1;
    entries.forEach(([uid, st]) => { const bd = st.bossDmg || 0; if (bd > best) { best = bd; mvpUid = uid; } });
    if (best > 0 && mvpUid === user?.id) {
      mvpPostedRef.current = true;
      api.post("/rooms/coop/profile-stats", { mvp: true })
        .then(({ data }) => (data?.newly_unlocked || []).forEach((a, i) => setTimeout(() => toast.success(`${a.icon} Achievement unlocked — ${a.name}!`, { description: "Saved to your Co-op record." }), i * 700)))
        .catch(() => {});
    }
  }, [open, coopMode, squadStats, user?.id]);
  if (!room) return null;
  const match = room.match || null;
  const isFps = !!match;
  const isTeam = match?.kind === "team";
  const results = [...(room.results || [])].sort((a, b) => (a.place || 99) - (b.place || 99));
  const myResult = results.find((r) => r.user_id === user?.id);
  const totalPlayers = (room.members || []).length;
  const waiting = Math.max(0, totalPlayers - results.length);
  const shareUrl = `${window.location.origin}/app/coop/${room.id}`;
  const placeLabel = (pl) => MEDALS[pl - 1] ? `${MEDALS[pl - 1]} ${["1st", "2nd", "3rd"][pl - 1]} place` : `#${pl} place`;

  const recap = coopMode ? Object.entries(squadStats || {}).map(([uid, st]) => {
    const mem = (room.members || []).find((m) => m.user_id === uid);
    return { uid, name: st.name || mem?.name || "Player", avatar: mem?.avatar || "🎮", kills: st.kills || 0, revives: st.revives || 0, roll: st.roll || 0, bossDmg: st.bossDmg || 0, score: st.score || 0 };
  }).sort((a, b) => (b.score || 0) - (a.score || 0)) : [];
  const mvpUid = recap.length ? recap.reduce((best, e) => ((e.bossDmg || 0) > (best.bossDmg || -1) ? e : best), recap[0]).uid : null;
  const bestRollUid = recap.length ? recap.reduce((best, e) => ((e.roll || 0) > (best.roll || -1) ? e : best), recap[0]).uid : null;
  const totalKills = recap.reduce((a, e) => a + (e.kills || 0), 0);
  const totalRevives = recap.reduce((a, e) => a + (e.revives || 0), 0);
  const mvpEntry = coopMode && mvpUid ? recap.find((e) => e.uid === mvpUid) : null;
  const showMvp = !!(mvpEntry && (mvpEntry.bossDmg || 0) > 0);
  const isMeMvp = showMvp && mvpUid === user?.id;

  const mvpCard = () => {
    const c = document.createElement("canvas"); c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 1200, 630); g.addColorStop(0, "#1a0606"); g.addColorStop(0.5, "#3a0d16"); g.addColorStop(1, "#0b1026");
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1200, 630);
    // dramatic radial flash
    const rad = ctx.createRadialGradient(600, 300, 40, 600, 300, 620); rad.addColorStop(0, "rgba(255,90,60,0.35)"); rad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = rad; ctx.fillRect(0, 0, 1200, 630);
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(255,209,102,0.95)"; ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText("👑 GAMEVIEW STUDIO · BOSS MVP", 60, 50);
    ctx.fillStyle = "#ff5a3c"; ctx.font = "900 96px Inter, system-ui, sans-serif";
    ctx.fillText("FINAL BLOW", 60, 130);
    ctx.font = "150px serif"; ctx.fillText(mvpEntry?.avatar || "🎮", 60, 270);
    ctx.fillStyle = "#fff"; ctx.font = "bold 60px Inter, system-ui, sans-serif";
    ctx.fillText((mvpEntry?.name || "Champion").slice(0, 18), 270, 300);
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 34px Inter, system-ui, sans-serif";
    ctx.fillText(`💥 ${mvpEntry?.bossDmg || 0} boss damage · sealed the kill`, 270, 380);
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText(`🐉 ${(gameTitle || "Boss Raid").slice(0, 30)}`, 270, 430);
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "26px Inter, system-ui, sans-serif";
    ctx.fillText("▶ Squad up — " + shareUrl.replace(/^https?:\/\//, "").slice(0, 46), 60, 578);
    c.toBlob((blob) => { if (!blob) { toast.error("Couldn't build the highlight — try again."); return; } const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = "mvp-final-blow.png"; a.click(); URL.revokeObjectURL(u); toast.success("MVP highlight card saved!"); });
  };
  const downloadClip = () => {
    if (!mvpClipUrl) return;
    const a = document.createElement("a"); a.href = mvpClipUrl; a.download = "mvp-final-blow." + ((mvpClipMime || "").indexOf("mp4") >= 0 ? "mp4" : "webm"); a.click();
    toast.success("Final-blow clip downloaded!");
  };
  const postClipToCommunity = async () => {
    if (!mvpClipBlob || uploadState === "uploading") return;
    if (!room?.game_id) { toast.error("Couldn't find the game for this clip."); return; }
    setUploadState("uploading");
    try {
      const ext = (mvpClipMime || "").indexOf("mp4") >= 0 ? "mp4" : "webm";
      const fd = new FormData();
      fd.append("game_id", room.game_id);
      fd.append("caption", `👑 ${mvpEntry?.name || "MVP"} landed the final blow on ${gameTitle || "the boss"}!`.slice(0, 200));
      fd.append("coop_mvp", "true");
      fd.append("clip", new File([mvpClipBlob], `mvp-final-blow.${ext}`, { type: mvpClipMime || "video/webm" }));
      const { data } = await api.post("/clips", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadState("done");
      if (data?.id && onClipPosted) onClipPosted(data.id);
      toast.success("Posted to Community Clips! 🎬");
    } catch (e) {
      setUploadState("idle");
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post — the game must be published to share clips.");
    }
  };

  const shareRecap = () => {
    const c = document.createElement("canvas"); c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 1200, 630); g.addColorStop(0, "#0b1026"); g.addColorStop(1, "#12324a");
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1200, 630);
    ctx.fillStyle = "rgba(124,247,255,0.9)"; ctx.textBaseline = "top"; ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText("🛡 GAMEVIEW STUDIO · CO-OP SQUAD RECAP", 60, 46);
    ctx.fillStyle = "#fff"; ctx.font = "bold 52px Inter, system-ui, sans-serif";
    ctx.fillText((gameTitle || "Squad Run").slice(0, 26), 60, 96);
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 34px Inter, system-ui, sans-serif";
    ctx.fillText(`💥 ${totalKills} kills   ·   🩹 ${totalRevives} revives   ·   👥 ${recap.length} squad`, 60, 168);
    let y = 240; const top = recap.slice(0, 5);
    top.forEach((e) => {
      ctx.fillStyle = "rgba(255,255,255,0.06)"; ctx.fillRect(60, y, 1080, 64);
      ctx.font = "40px serif"; ctx.fillStyle = "#fff"; ctx.fillText(e.avatar || "🎮", 78, y + 10);
      ctx.font = "bold 28px Inter, system-ui, sans-serif"; ctx.fillStyle = "#fff";
      ctx.fillText(e.name.slice(0, 16) + (e.uid === mvpUid ? "  👑 MVP" : ""), 140, y + 8);
      ctx.font = "22px Inter, system-ui, sans-serif"; ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText(`💥 ${e.kills}   🩹 ${e.revives}   🎲 ${e.roll || "—"}   ⭐ ${e.score}`, 140, y + 36);
      y += 76;
    });
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "26px Inter, system-ui, sans-serif";
    ctx.fillText("▶ Squad up — " + shareUrl.replace(/^https?:\/\//, "").slice(0, 46), 60, 596);
    c.toBlob((blob) => { if (!blob) { toast.error("Couldn't generate the card — try again."); return; } const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = "squad-recap.png"; a.click(); URL.revokeObjectURL(u); toast.success("Squad recap downloaded!"); });
  };

  const downloadCard = () => {
    const c = document.createElement("canvas"); c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 1200, 630); g.addColorStop(0, "#0b1026"); g.addColorStop(1, "#7c5cff");
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1200, 630);
    ctx.fillStyle = "rgba(0,0,0,0.4)"; ctx.fillRect(0, 0, 1200, 630);
    ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.textBaseline = "top"; ctx.font = "bold 32px Inter, system-ui, sans-serif";
    ctx.fillText("🏁 GAMEVIEW STUDIO · LIVE RACE", 60, 50);
    ctx.textBaseline = "middle"; ctx.font = "160px serif"; ctx.fillText(gameCover || "🏎️", 70, 330);
    ctx.fillStyle = "#fff"; ctx.textBaseline = "alphabetic"; ctx.font = "bold 56px Inter, system-ui, sans-serif";
    ctx.fillText((gameTitle || "Race").slice(0, 22), 300, 215);
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 90px Inter, system-ui, sans-serif";
    ctx.fillText(myResult ? placeLabel(myResult.place) : "Finished!", 300, 350);
    ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "bold 38px Inter, system-ui, sans-serif";
    ctx.fillText("Score " + (myResult ? myResult.score : 0), 300, 415);
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "28px Inter, system-ui, sans-serif";
    ctx.fillText("▶ Race your friends — " + shareUrl.replace(/^https?:\/\//, "").slice(0, 46), 60, 575);
    c.toBlob((blob) => { if (!blob) { toast.error("Couldn't generate the card — try again."); return; } const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = "race-result.png"; a.click(); URL.revokeObjectURL(u); toast.success("Result card downloaded!"); });
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Race link copied — challenge a friend!"); } catch { toast.error("Couldn't copy link."); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="coop-results">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> {coopMode ? "Squad Results" : isFps ? "Match Results" : "Race Results"}</DialogTitle></DialogHeader>

        {coopMode && recap.length > 0 && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 space-y-2" data-testid="coop-squad-recap">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]"><Users className="w-4 h-4" /> Squad recap</span>
              <span className="tabular-nums text-[hsl(var(--muted-foreground))]">💥 {totalKills} · 🩹 {totalRevives}</span>
            </div>
            {recap.map((e) => (
              <div key={e.uid} className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm ${e.uid === user?.id ? "bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/40" : "border border-[hsl(var(--border))]"}`} data-testid={`coop-recap-${e.uid}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="text-lg">{e.avatar}</span>
                  <span className="font-semibold truncate">{e.name}{e.uid === user?.id && " (you)"}</span>
                  {e.uid === mvpUid && e.bossDmg > 0 && <span title="Boss MVP" className="text-[11px] font-bold text-amber-400">👑 MVP</span>}
                  {e.uid === bestRollUid && e.roll > 0 && <span title="Best roll" className="text-[11px] font-bold text-sky-400">🎲 {e.roll}</span>}
                </span>
                <span className="font-mono tabular-nums text-xs text-[hsl(var(--muted-foreground))]">💥{e.kills} 🩹{e.revives} ⭐{e.score}</span>
              </div>
            ))}
          </div>
        )}

        {showMvp && (
          <div className="rounded-xl border border-amber-500/50 bg-gradient-to-br from-rose-500/10 to-amber-500/10 p-3 space-y-2.5" data-testid="coop-mvp-highlight">
            <div className="flex items-center gap-2 text-sm font-bold text-amber-400">
              <Crown className="w-4 h-4" /> {isMeMvp ? "Your Final Blow" : "Boss MVP — Final Blow"}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-2xl">{mvpEntry.avatar}</span>
              <span className="min-w-0">
                <span className="font-semibold truncate block">{mvpEntry.name}{isMeMvp && " (you)"}</span>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">💥 {mvpEntry.bossDmg} boss damage · sealed the kill</span>
              </span>
            </div>
            {isMeMvp && mvpClipUrl && !squadClip ? (
              <video src={mvpClipUrl} controls autoPlay loop muted playsInline className="w-full rounded-lg border border-[hsl(var(--border))] bg-black" data-testid="coop-mvp-clip-video" />
            ) : isMeMvp && !mvpClipUrl ? (
              <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="coop-mvp-clip-fallback">Grab your shareable highlight card below — the final-blow video wasn't captured this run.</p>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              {isMeMvp && mvpClipUrl && (
                <Button variant="outline" onClick={downloadClip} className="rounded-full gap-1.5" data-testid="coop-mvp-clip-download"><Film className="w-4 h-4" /> Save clip</Button>
              )}
              <Button variant="outline" onClick={mvpCard} className={`rounded-full gap-1.5 ${isMeMvp && mvpClipUrl ? "" : "col-span-2"}`} data-testid="coop-mvp-card-btn"><Share2 className="w-4 h-4" /> Highlight card</Button>
            </div>
            {isMeMvp && mvpClipBlob && (
              uploadState === "done" ? (
                <p className="text-[12px] text-emerald-400 font-semibold flex items-center gap-1.5" data-testid="coop-mvp-posted"><Check className="w-3.5 h-3.5" /> Posted — find it on the Community Clips feed!</p>
              ) : (
                <Button onClick={postClipToCommunity} disabled={uploadState === "uploading"} className="w-full rounded-full gap-1.5 bg-gradient-to-r from-fuchsia-500 to-violet-500" data-testid="coop-mvp-post-community">
                  {uploadState === "uploading" ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting…</> : <><Globe2 className="w-4 h-4" /> Post to Community Clips</>}
                </Button>
              )
            )}
          </div>
        )}

        {coopMode && squadClip?.id && (
          <ClipReactions clipId={squadClip.id} uploaderName={squadClip.name} />
        )}

        {isTeam && room.winner?.team && (
          <div className={`rounded-xl border px-3 py-2 text-center font-bold ${room.winner.team === "red" ? "border-rose-500/40 bg-rose-500/10 text-rose-500" : room.winner.team === "blue" ? "border-sky-500/40 bg-sky-500/10 text-sky-500" : "border-[hsl(var(--border))]"}`} data-testid="coop-team-winner">
            {room.winner.avatar} {room.winner.name} wins{room.winner.team !== "draw" ? ` with ${room.winner.score} kills!` : "!"}
          </div>
        )}

        <div className="space-y-2" data-testid="coop-results-podium" style={coopMode && recap.length ? { display: "none" } : undefined}>
          {results.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{isFps ? "Match over! Tallying frags…" : "Finishing line crossed! Waiting for final standings…"}</p>
          ) : results.map((r, i) => (
            <div key={r.user_id} className={`flex items-center justify-between rounded-xl border px-3 py-2 ${r.user_id === user?.id ? "border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))]"}`} data-testid={`coop-result-${i}`}>
              <span className="flex items-center gap-2 truncate">
                <span className="w-7 text-center text-lg">{MEDALS[r.place - 1] || r.place}</span>
                {r.team && <span>{r.team === "red" ? "🔴" : "🔵"}</span>}
                <span>{r.avatar}</span>
                <span className="font-semibold truncate">{r.name}{r.user_id === user?.id && " (you)"}</span>
                {i === 0 && <Crown className="w-4 h-4 text-amber-400 shrink-0" />}
              </span>
              <span className="font-mono tabular-nums text-sm">{isFps ? `${r.score} 💀${r.deaths != null ? ` / ${r.deaths} ☠` : ""}` : r.score}</span>
            </div>
          ))}
          {waiting > 0 && !isFps && (
            <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 pt-1"><Clock className="w-3.5 h-3.5" /> Waiting for {waiting} more racer{waiting > 1 ? "s" : ""} to finish…</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" onClick={coopMode ? shareRecap : downloadCard} className="rounded-full gap-1.5" data-testid="coop-result-share"><Download className="w-4 h-4" /> {coopMode ? "Share recap" : "Share card"}</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5" data-testid="coop-result-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
        </div>
        <div className="flex gap-2">
          {isHost ? (
            <Button onClick={onRematch} className="rounded-full gap-1.5 flex-1" data-testid="coop-rematch-btn"><RotateCcw className="w-4 h-4" /> Rematch</Button>
          ) : (
            <Button disabled className="rounded-full gap-1.5 flex-1" data-testid="coop-rematch-waiting"><RotateCcw className="w-4 h-4" /> Waiting for host…</Button>
          )}
          <Button variant="ghost" onClick={onLeave} className="rounded-full gap-1.5" data-testid="coop-results-leave"><LogOut className="w-4 h-4" /> Leave</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
